import { RippleIcon } from '@dealstack/frontend/shared/components/RippleIcon/RippleIcon';
import { Flex, FlexProps, Text } from '@mantine/core';
import { PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import { RippleColor } from '@dealstack/frontend/shared/components/RippleIcon/RippleIcon.types';

interface Props {
	title?: string;
	description?: ReactNode | string;
	iconVariant?: PredefinedIcons;
	direction?: 'row' | 'column';
	rippleColor?: RippleColor;
	lightDescription?: boolean;
	alignContent?: FlexProps['align'];
	fullWidthChildren?: boolean;
	/**
	 * Tints the border to match `rippleColor` instead of the neutral default, for boxes
	 * that carry a state (a warning, an error) rather than plain guidance.
	 */
	matchBorderToRippleColor?: boolean;
	dataTestId?: string;
}

export const InfoBox = ({
	title,
	description,
	iconVariant,
	children,
	direction,
	rippleColor,
	lightDescription,
	alignContent = 'start',
	fullWidthChildren,
	matchBorderToRippleColor,
	dataTestId,
}: PropsWithChildren<Props>) => {
	return (
		<InfoBoxContainer
			direction="column"
			gap="1rem"
			$borderColor={matchBorderToRippleColor ? rippleColor : undefined}
			data-testid={dataTestId}
		>
			<Flex direction={direction} align={alignContent} gap="1rem">
				{iconVariant && <RippleIcon variant={iconVariant} color={rippleColor} />}
				<Flex direction="column" gap=".5rem" w="100%">
					<Flex direction="column" gap=".25rem">
						{title && (
							<Text variant="p-md" fw={600} c="dark.8">
								{title}
							</Text>
						)}
						{description && (
							<Text variant="p-md" c={lightDescription ? 'dark.0' : 'dark.6'}>
								{description}
							</Text>
						)}
					</Flex>
					{!fullWidthChildren && children}
				</Flex>
			</Flex>
			{fullWidthChildren && children}
		</InfoBoxContainer>
	);
};

export const InfoBoxContainer = styled(Flex)<FlexProps & { $borderColor?: RippleColor }>`
	width: 100%;
	padding: 1rem 0.75rem;
	border-radius: 0.5rem;
	border: 1px solid
		${({ $borderColor, theme }) =>
			$borderColor && $borderColor !== 'gray'
				? theme.v3.colors[$borderColor][2]
				: theme.v3.colors.gray[2]};
`;
